import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSubscription, getUsageToday, PLANS } from "../services/subscription";

const GUEST_USAGE_KEY = "guest_ai_usage";
const GUEST_PHOTO_KEY = "guest_photo_trials";

// Lifetime free photo analyses granted to guests — enough to experience the
// "snap a photo → get macros" wow-moment before hitting the Premium gate.
export const GUEST_PHOTO_TRIAL_LIMIT = 2;

function getGuestUsageToday() {
  try {
    const stored = JSON.parse(localStorage.getItem(GUEST_USAGE_KEY) || "{}");
    const today = new Date().toISOString().split("T")[0];
    if (stored.date !== today) return { aiCalls: 0, photoCalls: 0 };
    return { aiCalls: stored.aiCalls || 0, photoCalls: stored.photoCalls || 0 };
  } catch {
    return { aiCalls: 0, photoCalls: 0 };
  }
}

function getGuestPhotoTrialsUsed() {
  try {
    return Number(localStorage.getItem(GUEST_PHOTO_KEY)) || 0;
  } catch {
    return 0;
  }
}

export function trackGuestPhotoTrial() {
  try {
    localStorage.setItem(GUEST_PHOTO_KEY, String(getGuestPhotoTrialsUsed() + 1));
  } catch {
    /* ignore storage errors */
  }
}

export function trackGuestUsage() {
  const today = new Date().toISOString().split("T")[0];
  const stored = JSON.parse(localStorage.getItem(GUEST_USAGE_KEY) || "{}");
  if (stored.date !== today) {
    localStorage.setItem(GUEST_USAGE_KEY, JSON.stringify({ date: today, aiCalls: 1, photoCalls: 0 }));
  } else {
    stored.aiCalls = (stored.aiCalls || 0) + 1;
    localStorage.setItem(GUEST_USAGE_KEY, JSON.stringify(stored));
  }
}

/**
 * Hook to get the current user's subscription and usage status.
 * For guest users, tracks usage in localStorage.
 */
export const useSubscription = (uid) => {
  const isGuest = !uid || uid === "guest";
  const queryClient = useQueryClient();

  const subQuery = useQuery({
    queryKey: ["subscription", uid],
    queryFn: () => getSubscription(uid),
    enabled: !isGuest,
    staleTime: 1000 * 60 * 5,
  });

  const usageQuery = useQuery({
    queryKey: ["usage", uid],
    queryFn: () =>
      isGuest
        ? { ...getGuestUsageToday(), photoTrialsUsed: getGuestPhotoTrialsUsed() }
        : getUsageToday(uid),
    enabled: true,
    staleTime: isGuest ? 0 : 1000 * 30,
  });

  const plan = isGuest ? "free" : (subQuery.data?.plan || "free");
  const limits = PLANS[plan];
  const usage = usageQuery.data || { aiCalls: 0, photoCalls: 0 };

  return {
    plan,
    isPremium: plan === "premium",
    isActive: subQuery.data?.isActive || false,
    expiresAt: subQuery.data?.expiresAt || null,
    customerPortalUrl: subQuery.data?.customerPortalUrl || null,
    limits,
    usage,
    remaining: Math.max(0, limits.aiCallsPerDay - usage.aiCalls),
    photoTrialsRemaining: isGuest
      ? Math.max(0, GUEST_PHOTO_TRIAL_LIMIT - (usage.photoTrialsUsed || 0))
      : plan === "premium"
        ? Infinity
        : 0,
    photoTrialLimit: GUEST_PHOTO_TRIAL_LIMIT,
    isLoading: isGuest ? false : (subQuery.isLoading || usageQuery.isLoading),
    refetch: () => {
      queryClient.invalidateQueries({ queryKey: ["usage", uid] });
      if (!isGuest) {
        subQuery.refetch();
      }
    },
  };
};
